import { delay, put, select } from 'redux-saga/effects';
import i18n from 'i18n';
import * as Sentry from '@sentry/react';

import { updateActiveChipAction } from 'core/widgets/ActiveChip/actions';
import { addConfirmedBetAction, clearAllBetsAction } from 'core/widgets/Bet/actions';
import { getBetSum, confirmedBetsToObj, fillAllSlots } from 'core/widgets/Bet/utils';
import {
  saveBettingTimeToStoreAction,
  startDecrementTimeLeftAction,
} from 'core/widgets/BettingTime/actions';
import { changeRoundStateAction } from 'core/widgets/RoundStates/actions';
import { RoundStates } from 'core/widgets/RoundStates/types';
import { VideoQualities, StreamNamesType } from 'core/widgets/VideoSettings/types';
import { setInitialCounterStateAction } from 'core/widgets/Scoreboard/BallsCounter/actions';
import { setInitialScoreboardStateAction } from 'core/widgets/Scoreboard/Boards/actions';
import { saveTableSettingsToStoreAction } from 'core/widgets/TableSettings/actions';
import { setBalanceAction, setTotalBetAction } from 'core/widgets/User/actions';
import { Notifications } from 'types';
import { setStreamNamesAction } from 'core/widgets/VideoSettings/action';
import { confirmedBetSelector } from 'core/widgets/Bet/selectors';
import { BetType } from 'core/widgets/Bet/types';
import { setAvailableBetsAction } from 'core/widgets/Bet/BetAvailability';
import {
  setCurrentJackpot,
  setNextJackpot,
  setStageJackpot,
  setUserInfo,
} from 'core/widgets/Jackpot/actions';
import { setUserInfoAuth } from 'core/widgets/Auth/actions';
import { ACTIONS, IPlayerIdStage, PositionJackpot } from 'core/widgets/Jackpot/types';
import { RestService } from 'services/RestAPIService';
import { OnPlayerConnectedData } from '../types';
import { setCommissionModeAction } from 'core/widgets/Bet/CommissionMode/actions';
import { saveDrawResultToStoreAction } from 'core/widgets/DrawResult/actions';

export function* onPlayerConnectedSaga({
  type: notificationType,
  data: result,
  ...rest
}: OnPlayerConnectedData) {
  try {
    const {
      player,
      bets,
      availableBets,
      jackpotOn,
      mode,
      lobbyUrl,
      closeGameMessage,
      configuration: {
        table_id: tableId,
        table_name: tableName,
        displaying_win_result: displayingWinResult,
      },
      round,
      dealer: { nickname: dealerNickname },
      status: roundStatus,
      winners: winnersData,
      jackpots,
    } = result;

    const {
      balance,
      betsToken,
      apiToken,
      playerName,
      uuid,
      minBet: minBetLimit,
      maxBet: maxBetLimit,
      lang,
      currency,
      operatorName,
      baccaratLimits: tableLimits,
    } = player;

    const operatorId = operatorName.replace(/\s+/g, '-').toLowerCase();

    if (notificationType === Notifications.PLAYER_CONNECTED) {
      const { stream_high, stream_medium, stream_low } = result.configuration;
      const confirmedBets: BetType = yield select(confirmedBetSelector);
      Sentry.setTag('uid', uuid);
      Sentry.setTag('game_id', round?.roundId);
      Sentry.setTag('operator_id', operatorName);

      yield put(setUserInfo({ playerName, uuid }));
      yield put(
        setUserInfoAuth({
          playerName,
          uuid,
          currency: currency?.toUpperCase(),
          mode,
          jackpotOn,
          lang,
          operatorId,
          lobbyUrl,
          closeGameMessage,
        }),
      );

      yield put(
        saveTableSettingsToStoreAction({
          tableId,
          tableName,
          gameId: round?.roundId || null,
          dealerNickname,
          minBetLimit,
          maxBetLimit,
          displayingWinResult,
          tableLimits,
        }),
      );
      yield put(changeRoundStateAction({ roundState: roundStatus }));

      yield put(setBalanceAction(balance));

      yield put(setTotalBetAction(0));

      if (
        roundStatus === RoundStates.BettingTimeStarted &&
        round?.finishBettingTime !== undefined
      ) {
        const date = new Date(round.finishBettingTime);
        const dateNow = new Date();
        const difference = Math.floor((date.getTime() - dateNow.getTime()) / 1000);

        yield put(
          saveBettingTimeToStoreAction({
            bettingTimeEnd: round.finishBettingTime,
            bettingTime: difference,
          }),
        );
        yield put(startDecrementTimeLeftAction());
      }

      const language = localStorage.getItem(`${operatorId}_${tableId}_language`)
        ? JSON.parse(localStorage.getItem(`${operatorId}_${tableId}_language`) as string)
        : null;

      if (!language) {
        i18n.changeLanguage(lang);
      }

      const commissionMode = sessionStorage.getItem('commissionMode')
        ? JSON.parse(sessionStorage.getItem('commissionMode') as string)
        : null;

      if (commissionMode !== null) {
        yield put(setCommissionModeAction(JSON.parse(commissionMode)));
      }

      if (confirmedBets && Object.keys(confirmedBets).length) {
        yield put(clearAllBetsAction());
      }

      if (winnersData.length) {
        const winnersArray = winnersData.map((winner) => winner.winner);
        yield put(setInitialCounterStateAction({ winnersData: winnersArray }));
        yield put(setInitialScoreboardStateAction({ winnersData }));
      }

      if (round?.cards !== undefined && Object.keys(round?.cards).length) {
        const { cards, playerTotal, bankerTotal } = round;
        yield put(
          saveDrawResultToStoreAction({
            cards,
            points: { player: playerTotal, banker: bankerTotal },
          }),
        );
      }

      if (bets !== undefined && roundStatus !== RoundStates.ResultConfirmed) {
        const totalBet = getBetSum(confirmedBetsToObj(bets));

        yield put(addConfirmedBetAction(fillAllSlots(bets)));
        yield put(setTotalBetAction(totalBet));
      }

      if (availableBets && availableBets.length > 0) {
        yield put(setAvailableBetsAction(availableBets));
      }

      localStorage.setItem(`${operatorId}_${tableId}_rest_auth_token`, betsToken);
      localStorage.setItem(`${operatorId}_${tableId}_api_token`, apiToken);

      RestService.setTableID(tableId as string);
      RestService.setOperatorID(operatorId as string);

      yield put(updateActiveChipAction());

      if (stream_high && stream_medium && stream_low) {
        const streamNames: StreamNamesType = {
          [VideoQualities.High]: stream_high,
          [VideoQualities.Medium]: stream_medium,
          [VideoQualities.Low]: stream_low,
        };

        yield put(setStreamNamesAction(streamNames));
      }

      if (jackpots?.length) {
        const currentJackpot = jackpots?.find(
          (jackpot) => jackpot.position === PositionJackpot.CURRENT,
        );
        const nextJackpot = jackpots?.find((jackpot) => jackpot.position === PositionJackpot.NEXT);

        if (nextJackpot) {
          yield put(setNextJackpot(nextJackpot));
        }

        if (currentJackpot) {
          yield put(setCurrentJackpot(currentJackpot));

          if (currentJackpot.draw) {
            const playersObject = currentJackpot.draw.players?.length
              ? currentJackpot.draw.players?.reduce(
                  (acc, player) => ({
                    ...acc,
                    [player.uuid]: player,
                  }),
                  {} as { [key: string]: IPlayerIdStage },
                )
              : null;

            if (playersObject) {
              yield put(
                setStageJackpot({ playersObject, stageNumber: currentJackpot.draw.stageNumber }),
              );
            } else {
              yield put(
                setStageJackpot({
                  playersObject: null,
                  stageNumber: currentJackpot.draw.stageNumber,
                }),
              );
            }
          }

          yield delay(3000);

          yield put({ type: ACTIONS.WATCH_DATE_CONTROL_INTERVAL });
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error);
  }
}
